import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

interface UserTableProps {
  users: any[];
  onActionClick: (user: any, action: 'role' | 'deactivate') => void;
  onEditClick: (user: any) => void;
  isLoading: boolean;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  onActionClick,
  onEditClick,
  isLoading,
}) => {
  const getRoleColor = (role: string): any => {
    switch (role) {
      case 'admin': return 'error';
      case 'staff': return 'warning';
      case 'contractor': return 'info';
      case 'customer': return 'success';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'staff': return 'Mitarbeiter';
      case 'contractor': return 'Dienstleister';
      case 'customer': return 'Kunde';
      case 'deactivated': return 'Deaktiviert';
      case 'pending': return '⏳ Ausstehend';
      default: return role;
    }
  };

  const isActive = (user: any) => user.role !== 'deactivated';

  if (isLoading) return <CircularProgress />;

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>E-Mail</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Unternehmen</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Rolle</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Beigetreten</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id} hover>
              <TableCell>{user.name}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.company_name || '-'}</TableCell>
              <TableCell>
                <Chip
                  label={getRoleLabel(user.role)}
                  color={getRoleColor(user.role)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={isActive(user) ? 'Aktiv' : 'Inaktiv'}
                  color={isActive(user) ? 'success' : 'error'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {new Date(user.created_at).toLocaleDateString('de-DE')}
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => onEditClick(user)}
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onActionClick(user, 'role')}
                  >
                    Rolle
                  </Button>
                  <Button
                    variant="outlined"
                    color={isActive(user) ? 'error' : 'success'}
                    size="small"
                    onClick={() => onActionClick(user, 'deactivate')}
                  >
                    {isActive(user) ? 'Deaktivieren' : 'Aktivieren'}
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default UserTable;
